import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Screen } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { LinkButton } from '@/components/ui/LinkButton';
import { Banner, Card, EmptyState, ProgressBar, Spinner } from '@/components/ui/Card';
import { useAppData } from '@/services/data/dataContext';
import { useAsync } from '@/hooks/useAsync';
import { computeSceneStatistics, strugglingLineIds } from '@/services/progress/progressService';
import { pluralize } from '@/lib/text';

const MODES = [
  {
    id: 'cue',
    name: 'Cue Mode',
    description: 'Hear the last few words before your line, then recall it.',
  },
  {
    id: 'scene',
    name: 'Full Scene',
    description: 'Every other character read aloud, pausing on your lines.',
  },
  {
    id: 'speed',
    name: 'Speed Run',
    description: 'Rapid-fire cues for drilling what you already know.',
  },
  {
    id: 'missed',
    name: 'Missed Lines',
    description: 'Only what you missed, hesitated on, or had to reveal.',
  },
] as const;

function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      className="text-ink-400 size-5 shrink-0"
      aria-hidden="true"
    >
      <path d="m9 5 7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


export function PracticeScreen() {
  const { showId: routeShowId } = useParams();
  const { data } = useAppData();
  const [selectedScenes, setSelectedScenes] = useState<string[]>([]);
  const [scopeOpen, setScopeOpen] = useState(false);

  const context = useAsync(async () => {
    const shows = await data.listShows();
    const showId = routeShowId ?? shows[0]?.showId;
    if (!showId) return { show: null, script: null, struggling: 0, scenes: [] };

    const [show, script, progress] = await Promise.all([
      data.getShow(showId),
      data.getScript(showId),
      data.getProgress(showId),
    ]);
    return {
      show,
      script,
      struggling: strugglingLineIds(progress).size,
      // Already filtered to scenes the actor actually appears in.
      scenes: show && script ? computeSceneStatistics(script, progress, show.myRoles) : [],
    };
  }, [data, routeShowId]);

  if (context.loading) return <Spinner label="Loading rehearsal options" />;

  const show = context.data?.show;
  const script = context.data?.script;

  if (!show || !script) {
    return (
      <>
        <TopBar title="Practice" />
        <Screen>
          <EmptyState
            title="Nothing to rehearse yet"
            description="Add a show and pick your character first."
            action={
              <LinkButton to="/shows/new" size="lg">
                Add a show
              </LinkButton>
            }
          />
        </Screen>
      </>
    );
  }

  if (show.myRoles.length === 0) {
    return (
      <>
        <TopBar title="Practice" subtitle={show.title} />
        <Screen>
          <Banner tone="warn">Choose the character you are playing before rehearsing.</Banner>
          <LinkButton to={`/shows/${show.showId}`} size="lg" block>
            Choose my role
          </LinkButton>
        </Screen>
      </>
    );
  }

  const sceneQuery = selectedScenes.length > 0 ? `?scenes=${selectedScenes.join(',')}` : '';
  const struggling = context.data?.struggling ?? 0;
  const scenes = context.data?.scenes ?? [];

  const toggleScene = (sceneId: string) => {
    setSelectedScenes((current) =>
      current.includes(sceneId) ? current.filter((id) => id !== sceneId) : [...current, sceneId],
    );
  };

  const inScope = selectedScenes.length > 0
    ? scenes.filter((scene) => selectedScenes.includes(scene.sceneId))
    : scenes;
  const linesInScope = inScope.reduce((total, scene) => total + scene.totalLines, 0);
  const scopeLabel =
    selectedScenes.length === 0
      ? 'Whole show'
      : `${selectedScenes.length} ${pluralize(selectedScenes.length, 'scene')}`;

  const modeCount = (id: string) => (id === 'missed' ? struggling : linesInScope);

  return (
    <>
      <TopBar title="Practice" subtitle={`${show.title} · ${show.myRoles.join(', ')}`} />
      <Screen>
        <Card className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setScopeOpen((open) => !open)}
            aria-expanded={scopeOpen}
            className="flex min-h-11 w-full items-center justify-between gap-3 text-left"
          >
            <span>
              <span className="text-ink-500 dark:text-ink-400 block text-xs font-semibold uppercase">
                Scope
              </span>
              <span className="block font-semibold">
                {scopeLabel} · {linesInScope} {pluralize(linesInScope, 'line')}
              </span>
            </span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className={`text-ink-400 size-5 shrink-0 transition-transform ${scopeOpen ? 'rotate-90' : ''}`}
              aria-hidden="true"
            >
              <path d="m9 5 7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {scopeOpen ? (
            <>
              <p className="text-ink-500 dark:text-ink-400 text-sm">
                Only the {scenes.length} {pluralize(scenes.length, 'scene')} you appear in. Tap to
                narrow the rehearsal.
              </p>
              <ul className="flex flex-col gap-2">
                {scenes.map((scene) => {
                  const active = selectedScenes.includes(scene.sceneId);
                  return (
                    <li key={scene.sceneId}>
                      <button
                        type="button"
                        aria-pressed={active}
                        onClick={() => toggleScene(scene.sceneId)}
                        className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left ${
                          active
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                            : 'border-ink-200 dark:border-ink-800'
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border ${
                            active
                              ? 'border-brand-600 bg-brand-600 text-white'
                              : 'border-ink-300 dark:border-ink-700'
                          }`}
                          aria-hidden="true"
                        >
                          {active ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="size-3.5">
                              <path d="m5 13 4 4L19 7" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : null}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                          <span className="text-sm font-medium">{scene.name}</span>
                          <span className="text-ink-500 dark:text-ink-400 text-xs">
                            {scene.totalLines} {pluralize(scene.totalLines, 'line')} ·{' '}
                            {scene.memorized} memorized
                          </span>
                          <ProgressBar
                            percent={scene.progressPercent}
                            label={`${scene.name} progress`}
                          />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {selectedScenes.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setSelectedScenes([])}
                  className="text-brand-600 dark:text-brand-400 min-h-11 text-sm font-medium"
                >
                  Rehearse the whole show
                </button>
              ) : null}
            </>
          ) : null}
        </Card>

        <Card className="p-0">
          <ul className="divide-ink-200 dark:divide-ink-800 divide-y">
            {MODES.map((mode) => {
              const count = modeCount(mode.id);
              const disabled = count === 0;
              const body = (
                <>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="font-semibold">
                      {mode.name}
                      <span className="text-ink-500 dark:text-ink-400 font-normal"> · {count}</span>
                    </span>
                    <span className="text-ink-500 dark:text-ink-400 text-sm">
                      {disabled
                        ? mode.id === 'missed'
                          ? 'Nothing to drill yet — rehearse a scene first.'
                          : 'No lines in the current scope.'
                        : mode.description}
                    </span>
                  </span>
                  {disabled ? null : <Chevron />}
                </>
              );

              return (
                <li key={mode.id}>
                  {disabled ? (
                    <div className="flex min-h-16 items-center gap-3 p-4 opacity-50">{body}</div>
                  ) : (
                    <Link
                      to={`/rehearse/${show.showId}/${mode.id}${sceneQuery}`}
                      className="hover:bg-ink-50 dark:hover:bg-ink-800/50 flex min-h-16 items-center gap-3 p-4"
                    >
                      {body}
                    </Link>
                  )}
                </li>
              );
            })}

            <li>
              <Link
                to={`/flashcards/${show.showId}`}
                className="hover:bg-ink-50 dark:hover:bg-ink-800/50 flex min-h-16 items-center gap-3 p-4"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="font-semibold">Flashcards &amp; Quiz</span>
                  <span className="text-ink-500 dark:text-ink-400 text-sm">
                    Cue on the front, your line on the back. Shuffle for a quiz.
                  </span>
                </span>
                <Chevron />
              </Link>
            </li>
          </ul>
        </Card>
      </Screen>
    </>
  );
}
